//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

//
// Hooks Angular http requests to make sure that all gateway API ajax requests are made
// with the api gateway token header set and clientId specified via a query param.
//

import { IHttpInterceptor, IQService, IRequestConfig } from 'angular';
// eslint-disable-next-line no-restricted-imports
import * as angular from 'angular';
import _ from 'underscore';

import { CuralateAuthTokenHeader } from '@constants/CuralateAuthTokenHeader';

import { getRequiredJwtToken } from 'app/initialization/amplify/auth';
import { SessionVarsService as TSessionVarsService } from 'app/services/session-vars-service';

class GatewayApiInterceptor implements IHttpInterceptor {
  constructor(
    private readonly $q: IQService,
    private readonly SessionVarsService: TSessionVarsService,
  ) {}

  private isGatewayApiCall(config: IRequestConfig): boolean {
    const baseUrls = this.SessionVarsService.getBaseUrls();
    if (!_.isObject(baseUrls)) {
      return false;
    }

    return Object.values(baseUrls).some((gatewayBaseUrl: string) =>
      config.url.startsWith(gatewayBaseUrl),
    );
  }

  // HTTP request interceptor: Called when a request is made (pre-request)
  public request = (config: IRequestConfig) => {
    const clientId = this.SessionVarsService.getClientId();

    if (this.isGatewayApiCall(config)) {
      // Add the API Gateway Token on to any request to a gateway api
      return this.$q.when(
        getRequiredJwtToken()
          .then((jwt) => {
            config.headers = {
              ...config.headers,
              [CuralateAuthTokenHeader]: jwt,
            };
            config.params = {
              ...config.params,
              clientId,
            };
            return config;
          })
          .catch(() => this.$q.reject('No valid session')),
      );
    }
    return config;
  };

  // HTTP response error interceptor: redirect to login on 401 from any gateway service
  public responseError = (rejection: {
    config: IRequestConfig;
    status: number;
  }) => {
    if (
      rejection.status === 401 &&
      this.isGatewayApiCall(rejection.config) &&
      window.location.pathname !== '/login'
    ) {
      window.location.href = '/login';
    }
    return this.$q.reject(rejection);
  };
}

angular
  .module('curalate')
  .service('gatewayApiInterceptor', [
    '$q',
    'SessionVarsService',
    GatewayApiInterceptor,
  ]);
